
import { useState, useEffect, useCallback } from 'react';
import { Product } from '@/types/common';
import { OptimizedLocalStorageService } from '@/services/optimizedLocalStorage';

const MAX_HISTORY_ITEMS = 20;

export const useBrowsingHistory = (userId: string = 'anonymous') => {
  const [browsingHistory, setBrowsingHistory] = useState<Product[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Load browsing history on mount
  useEffect(() => {
    const loadHistory = () => {
      try {
        const history = OptimizedLocalStorageService.getOptimizedBrowsingHistory(userId);
        const validProducts = history.filter(item => 
          item.product && 
          item.product.id && 
          item.product.name
        ).map(item => item.product);
        
        setBrowsingHistory(validProducts.slice(0, MAX_HISTORY_ITEMS));
      } catch (error) {
        console.error('Error loading browsing history:', error);
        setBrowsingHistory([]);
      } finally {
        setIsLoading(false);
      }
    };

    loadHistory();
  }, [userId]);

  // Add product to browsing history
  const addToHistory = useCallback((product: Product) => {
    if (!product?.id || !product?.name) return;

    setBrowsingHistory(prev => {
      // Remove if already exists to avoid duplicates
      const filtered = prev.filter(item => item.id !== product.id);
      
      // Add to beginning and limit size
      const newHistory = [product, ...filtered].slice(0, MAX_HISTORY_ITEMS);
      
      // Save to localStorage
      try {
        const historyData = newHistory.map(item => ({
          product_id: item.id,
          product: item,
          viewed_at: new Date().toISOString()
        }));
        
        OptimizedLocalStorageService.setOptimizedBrowsingHistory(userId, historyData);
      } catch (error) {
        console.error('Error saving browsing history:', error);
      }
      
      return newHistory;
    });
  }, [userId]);

  // Remove product from browsing history
  const removeFromHistory = useCallback((productId: string) => {
    setBrowsingHistory(prev => {
      const newHistory = prev.filter(item => item.id !== productId);
      
      // Save to localStorage
      try {
        const historyData = newHistory.map(item => ({
          product_id: item.id,
          product: item,
          viewed_at: new Date().toISOString()
        }));
        
        OptimizedLocalStorageService.setOptimizedBrowsingHistory(userId, historyData);
      } catch (error) {
        console.error('Error updating browsing history:', error);
      }
      
      return newHistory;
    });
  }, [userId]);

  // Clear all browsing history
  const clearHistory = useCallback(() => {
    setBrowsingHistory([]);
    try {
      OptimizedLocalStorageService.setOptimizedBrowsingHistory(userId, []);
    } catch (error) {
      console.error('Error clearing browsing history:', error);
    }
  }, [userId]);

  return {
    browsingHistory,
    isLoading,
    addToHistory,
    removeFromHistory,
    clearHistory,
    hasHistory: browsingHistory.length > 0
  };
};
